import { FormBuilder } from '@angular/forms';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FilterPanelEditorComponent } from 'src/ca-shared/filter-panel/models/filter-panel-editor.component';
import { FilterPanelEditorConfigurationModel } from 'src/ca-shared/filter-panel/models/filter-panel-editor-configuration.model';
import { CategorySelectionConfigurationModel } from '../models/category-selection-configuration.model';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { CategoryDto } from 'src/core/models/query/category.dto';
import { Operators } from 'src/core/models/request/operator.enum';
import { FilterItemDto } from 'src/core/models/request/filter-item.dto';
import { SelectorComponent } from 'src/ca-shared/selector/selector.module';
import { ColorUtils } from 'src/core/helpers/color-utils';
import { combineLatest, map, of, Subscription } from 'rxjs';
import { CrudService } from 'src/core/services/crud/crud.service';
import { QueryService } from 'src/core/services/query/query.service';
import { QueryDto } from 'src/core/models/query/query.dto';
import { QueryItemDto } from 'src/core/models/query/query-item.dto';
import { TopicService } from 'src/core/services/ai-topic/topic.service';
import { TopicClusterKeywordDto } from 'src/core/models/conversation/ai-topic/topic-cluster-keyword.dto';
import { CategoryType } from 'src/core/models/enum/category-type.enum';
import { SorterItemDto } from 'src/core/models/request/sorter-item.dto';
import { QueryTupleDto } from 'src/core/models/query/query-tuple.dto';
import { Store } from '@ngxs/store';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { CategorySortType } from 'src/core/models/generic-lookup-type/category/category-sort-type.glt';
import { CANumberPipe } from 'src/core/pipes/ca-number.pipe';
import { CADatePipe } from 'src/core/pipes/ca-date.pipe';
import { LocalizationService } from '@abp/ng.core';
import { QueryIndexStatusDto } from 'src/core/models/query/query-index-status.dto';
import { DateDisplayType } from 'src/ca-shared/conversation-date-filter/models/date-display-type.enum';
import { CategorySelectorItemDto } from 'src/core/models/query/category-selector-item.dto';

@Component({
  selector: 'ca-category-selection-panel',
  templateUrl: './category-selection-panel.component.html',
  styleUrls: ['./category-selection-panel.component.scss'],
  standalone: false,
})
export class CategorySelectionPanelComponent extends FilterPanelEditorComponent implements OnInit {
  actualConfig: CategorySelectionConfigurationModel;
  _value: CategoryDto[];
  categoryDropdownOptions: any;
  isQueryPreviewLoading: boolean;
  previewQueryId: number;
  queryPreviewDataCache = new Map<number, QueryTupleDto>();
  queryPreviewDataSubscription: Subscription;
  isTopicPreviewLoading: boolean;
  previewTopicId: number;
  topicPreviewDataCache = new Map<number, TopicClusterKeywordDto[]>();

  @ViewChild(SelectorComponent, { read: SelectorComponent }) categorySelector: SelectorComponent;

  get categoryType(): typeof CategoryType {
    return CategoryType;
  }

  set config(value: FilterPanelEditorConfigurationModel) {
    this.actualConfig = value as CategorySelectionConfigurationModel;
  }

  get config(): FilterPanelEditorConfigurationModel {
    return this.actualConfig;
  }

  set value(value: any) {
    this.editorForm.patchValue({
      editor: value,
    });
  }

  get value(): any {
    return this._value;
  }

  sortItems: { id: number; label: string }[] = [];

  private innerLoad() {
    const filters: FilterItemDto[] = [];
    const sorters: SorterItemDto[] = [this.getSortItem()];

    if (this.extraData.conversationType && this.extraData.conversationType.length > 0) {
      filters.push({
        field: 'conversationType',
        operator: this.operators.In,
        value: this.extraData.conversationType,
      });
    }

    filters.push({
      field: 'isActive',
      operator: this.operators.Equals,
      value: true,
    });

    filters.push({
      field: 'notMerged',
      operator: this.operators.Equals,
      value: true,
    });

    if (this.actualConfig.onlyIncludeInReports === true) {
      filters.push({
        field: 'isCategory',
        operator: this.operators.Equals,
        value: true,
      });
    }

    if (this.actualConfig.onlyNonFcrGroupByCategory === true) {
      filters.push({
        field: 'onlyNonFcrGroupByCategory',
        operator: this.operators.Equals,
        value: true,
      });
    }

    if (this.actualConfig.includeUncategorized) {
      filters.push({
        field: 'includeUncategorized',
        operator: this.operators.Equals,
        value: true,
      });
    }

    this.categorySelector.setSorters(sorters);
    this.categorySelector.setFilters(filters);
    this.categorySelector.refresh();
  }

  autoSelectFirstItem(): void {}

  reload(): void {
    this.innerLoad();
  }

  reset(): void {
    this.getSortItem();
    this.categorySelector.currentPage = 1;
  }

  init() {
    super.init();

    if (this.actualConfig.autoLoad) {
      this.actualConfig.autoLoad = false;
      this.innerLoad();
    }
  }

  getIndexSummaryMessage(indexSummary: QueryIndexStatusDto): string {
    let message = this.localizationService.instant('Query::IndexSummaryMessage');
    const percentage = indexSummary?.totalConversationCount
      ? (indexSummary?.totalProcessedCount / indexSummary?.totalConversationCount) * 100
      : 0;
    const ratio = this.caNumberPipe.transform(percentage);
    message = message.replace('{0}', ratio);
    message = message.replace(
      '{1}',
      this.caDatePipe.transform(indexSummary?.indexingStartDate, DateDisplayType.Date, true)
    );
    message = message.replace(
      '{2}',
      this.caDatePipe.transform(indexSummary?.indexingEndDate, DateDisplayType.Date, true)
    );
    return message;
  }

  toggleQueryPreviewPopover(
    popover: NgbPopover,
    category: CategorySelectorItemDto,
    event: any
  ): boolean {
    this.previewQueryId = category.queryId;

    if (popover.isOpen()) {
      popover.close();
      this.previewQueryId = 0;
    } else {
      const data = this.queryPreviewDataCache.get(category.queryId);
      if (data == null) {
        this.isQueryPreviewLoading = true;
        const categoriesRequest = this.queryService.getQueryCategories();
        const queryItemsRequest = this.service.getById<QueryItemDto[]>(
          QueryDto,
          category.queryId,
          'items'
        );

        const queryRequest = this.service.getById<QueryDto>(QueryDto, category.queryId);
        const indexSummaryRequest = category.lastQueryVersionIndexed
          ? of(null)
          : this.queryService.getQueryIndexSummary(category.queryId);
        this.queryPreviewDataSubscription = combineLatest([
          categoriesRequest,
          queryItemsRequest,
          queryRequest,
          indexSummaryRequest,
        ]).subscribe(([categories, queryItems, query, indexSummary]) => {
          const previewData: QueryTupleDto = {
            queryBuilderCategoryData: categories.items,
            queryItems: queryItems,
            query: query,
          };

          this.queryPreviewDataCache.set(category.queryId, previewData);
          this.isQueryPreviewLoading = false;
          popover.open({
            query: query,
            queryItems: queryItems,
            categories: categories.items,
            indexSummary: indexSummary,
          });
        });
      } else {
        if (!data?.query?.lastQueryVersionIndexed) {
          this.isQueryPreviewLoading = true;
          this.queryService.getQueryIndexSummary(category.queryId).subscribe(indexSummary => {
            this.isQueryPreviewLoading = false;
            if (
              indexSummary?.totalConversationCount &&
              indexSummary?.totalProcessedCount >= indexSummary?.totalConversationCount
            ) {
              data.query.lastQueryVersionIndexed = data.query.lastQueryVersionId;
              const previewData: QueryTupleDto = {
                queryBuilderCategoryData: data.queryBuilderCategoryData,
                queryItems: data.queryItems,
                query: data.query,
              };
              this.queryPreviewDataCache.set(category.queryId, previewData);
            }
            popover.open({
              query: data.query,
              queryItems: data.queryItems,
              categories: data.queryBuilderCategoryData,
              indexSummary: indexSummary,
            });
          });
        } else {
          popover.open({
            query: data.query,
            queryItems: data.queryItems,
            categories: data.queryBuilderCategoryData,
          });
        }
      }
    }

    event.stopPropagation();
    return false;
  }

  toggleTopicPreviewPopover(popover: NgbPopover, category: CategoryDto, event: any): boolean {
    this.previewTopicId = category.topicId;

    if (popover.isOpen()) {
      popover.close();
      this.previewTopicId = 0;
    } else {
      const data = this.topicPreviewDataCache.get(category.topicId);
      if (data == null) {
        this.isTopicPreviewLoading = true;

        this.topicService.getTopicKeywordsById(category.topicId).subscribe(data => {
          this.topicPreviewDataCache.set(category.topicId, data);
          this.isTopicPreviewLoading = false;
          popover.open({ phrases: data.map(k => k.token) });
        });
      } else {
        popover.open({ phrases: data.map(k => k.token) });
      }
    }

    event.stopPropagation();
    return false;
  }

  constructor(
    private operators: Operators,
    public colorUtils: ColorUtils,
    private service: CrudService,
    private queryService: QueryService,
    private topicService: TopicService,
    private store: Store,
    protected fb: FormBuilder,
    private caNumberPipe: CANumberPipe,
    private caDatePipe: CADatePipe,
    private localizationService: LocalizationService
  ) {
    super(fb);

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(map(filterFn => filterFn(CategorySortType)))
      .subscribe(result => {
        this.sortItems = result.map(x => {
          return { id: x.id, label: x.code };
        });
      });

    this.editorForm.valueChanges.subscribe(val => {
      this._value = val.editor;
      this.onChange();
    });
  }

  ngOnInit(): void {
    let sorters: SorterItemDto[] = [];

    sorters.push(this.getSortItem());

    this.categoryDropdownOptions = {
      pageSize: 9999,
      queryOperator: this.operators.Contains,
      queryField: 'quickSearchTerm',
      url: 'api/app/category/selector-data',
      currentPage: 1,
      showSelections: this.actualConfig.multiple,
      showClearSelections: this.actualConfig.multiple,
      showClearSelectionLink: !this.actualConfig.multiple,
      selectorPosition: 'Top',
      sorters: sorters,
    };
  }

  private getSortItem(): SorterItemDto {
    if (!this.actualConfig.allowSortChange) {
      return {
        field: 'Name',
        direction: 'ASC',
      };
    }

    if (!this.extraData.sortType) {
      this.setExtraData('sortType', CategorySortType.byName);
    }

    return {
      field: this.extraData.sortType == CategorySortType.byName ? 'Name' : 'MatchingCount',
      direction: this.extraData.sortType == CategorySortType.byName ? 'ASC' : 'DESC',
    };
  }
}
